import { ReactNode } from 'react';
import ReactModal from 'react-modal';

import { useAppInerte } from 'utils/app-inerte';
import { FOCO_VISIVEL } from 'utils/foco';

interface ModalDoCatalogoProps {
  isOpen: boolean;
  /** `id` do título da etapa visível: o nome acessível do diálogo. */
  tituloId: string;
  /** `id` do texto que descreve a etapa visível. */
  descricaoId?: string;
  role?: 'dialog' | 'alertdialog';
  /** Sem ela, nem ✕, nem Esc: o modal só fecha pelas próprias ações. */
  onFechar?: () => void;
  rotuloFechar?: string;
  children: ReactNode;
}

/**
 * Moldura dos modais do catálogo, a partir do "Escolha registrada!" do design: véu escuro, cartão
 * branco de raio 24px e ✕ no canto.
 *
 * Enquanto aberto, a aplicação (`#root`) fica `inert` pelo `useAppInerte`, o mesmo do `ModalFrame`:
 * sai do Tab e da árvore de acessibilidade, e modais empilhados liberam a página só no último.
 */
export default function ModalDoCatalogo({
  isOpen,
  tituloId,
  descricaoId,
  role = 'dialog',
  onFechar,
  rotuloFechar = 'Fechar',
  children,
}: ModalDoCatalogoProps) {
  useAppInerte(isOpen);

  return (
    <ReactModal
      isOpen={isOpen}
      ariaHideApp={false}
      onRequestClose={onFechar}
      shouldCloseOnOverlayClick={!!onFechar}
      shouldCloseOnEsc={!!onFechar}
      shouldFocusAfterRender
      shouldReturnFocusAfterClose
      role={role}
      aria={{ labelledby: tituloId, describedby: descricaoId }}
      portalClassName="relative z-50"
      overlayClassName="fixed inset-0 z-[100] flex items-center justify-center bg-[rgba(17,24,39,0.55)] p-4 sm:p-8"
      className={`relative box-border max-h-full w-full max-w-[592px] overflow-y-auto rounded-3xl bg-brand-white px-6 pb-[30px] pt-[34px] text-content shadow-[0_18px_50px_rgba(17,24,39,0.18)] outline-none sm:px-9`}
    >
      {onFechar && (
        <button
          type="button"
          onClick={onFechar}
          aria-label={rotuloFechar}
          className={`absolute right-[18px] top-[18px] flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent text-[16px] font-bold text-brand-blue-dark hover:bg-surface-100 ${FOCO_VISIVEL}`}
        >
          <span aria-hidden="true">✕</span>
        </button>
      )}
      {children}
    </ReactModal>
  );
}
